import { useContext, useState } from 'react';
import RestrictedToPortfolioMode from '@/components/RestrictedToPortfolioMode';
import PortfolioMode from '@/lib/constants/PortfolioMode.enum';
import { FormattedMessage } from 'react-intl';
import PhaseContext from '../../../PhaseContext';
import ChallengeContext from '../../../Challenge/ChallengeView/ChallengeContext';
import useApi from '@/lib/api/useApi';
import CreateButton from '../../../ChallengeBoard/KanbanPhaseColumn/CreateChallenge/CreateButton';
import Prompt from '@/components/Prompt';
import Dialog from '@/components/Dialog';
import FormattedMessageWithChallengeNaming from '@/components/FormattedMessageWithChallengeNaming';
import usePerspectivesEnabled from '@/lib/utils/usePerspectivesEnabled';
import PerspectiveAttachmentDialog from './PerspectiveAttachmentDialog';
import ChallengePortfolioContext from '@/components/ChallengePortfolio/ChallengePortfolioContext';
import {
  type AttachmentResult,
  type TaskSummary,
  type TaskWithoutRelations,
} from '@/lib/types';
import NewlySharedContext from '../../../NewlySharedChallenges/NewlySharedContext';
import ModelSelect from '../../../ChallengeBoard/KanbanPhaseColumn/CreateChallenge/ModelSelect';
import ModelType from '@/lib/constants/challengeModel/ModelType.enum';
import useDefaultModel from '../../../portfolio-item/PortfolioItemFields/usePortfolioItemModel/useDefaultModel';

enum CreateTaskStage {
  PERSPECTIVE_ATTACHMENT,
  NAME_PROMPT,
}

// TODO refactor, too large
const CreateTask = () => {
  const api = useApi();

  const [stage, setStage] = useState<CreateTaskStage | null>(null);

  const [isNewlyShared, , setNewlySharedChallenges] =
    useContext(NewlySharedContext);
  const [, setChallengePortfolio] = useContext(ChallengePortfolioContext);
  const [challenge, setChallenge] = useContext(ChallengeContext);
  const phase = useContext(PhaseContext);

  const perspectivesEnabled = usePerspectivesEnabled();

  const [attachedPerspectives, setAttachedPerspectives] = useState<number[]>(
    [],
  );
  const [attachedPerspectiveSublevels, setAttachedPerspectiveSublevels] =
    useState<number[]>([]);

  const defaultModel = useDefaultModel(ModelType.TASK);
  const [selectedModelId, setSelectedModelId] = useState<number | null>(
    defaultModel?.id ?? null,
  );

  const handleOpen = (): void => {
    setStage(
      perspectivesEnabled
        ? CreateTaskStage.PERSPECTIVE_ATTACHMENT
        : CreateTaskStage.NAME_PROMPT,
    );
  };

  const handleClose = (): void => {
    setStage(null);

    // Wait for the dialog animation to finish before resetting state. This
    //  prevents the input reset from happening visibly.
    setTimeout((): void => {
      setSelectedModelId(defaultModel?.id ?? null);
    }, 250);
  };

  const handleNext = (
    attachedPerspectives: number[],
    attachedPerspectiveSublevels: number[],
  ): void => {
    setAttachedPerspectives(attachedPerspectives);
    setAttachedPerspectiveSublevels(attachedPerspectiveSublevels);

    setStage(CreateTaskStage.NAME_PROMPT);
  };

  const createTask = async (name: string): Promise<void> => {
    const task = await api.post<TaskWithoutRelations & AttachmentResult>(
      `challenge-portfolio/tasks?challengeId=${challenge.id}${selectedModelId !== null ? `&modelId=${selectedModelId}` : ''}`,
      {
        name,
        phase,
        attachedPerspectives,
        attachedPerspectiveSublevels,
      },
    );

    const taskSummary = {
      ...task,
      thumbnail: null,
      assignees: [],
      worlds: [],
      tags: [],
      _count: {
        coachComments: 0,
      },
    };

    setChallenge(challenge => {
      challenge.tasks[task.id] = taskSummary;
    });

    if (isNewlyShared) {
      setNewlySharedChallenges(newlySharedChallenges => {
        (newlySharedChallenges[challenge.id].tasks as TaskSummary[]).push(
          taskSummary,
        );
      });

      return;
    }

    setChallengePortfolio(challengePortfolio => {
      // TODO it should be either an array or object, typing should be reworked
      (challengePortfolio.challenges[challenge.id].tasks as TaskSummary[]).push(
        taskSummary,
      );
    });
  };

  return (
    <RestrictedToPortfolioMode mode={PortfolioMode.STUDENT}>
      <CreateButton
        tooltip={<FormattedMessage id="portfolio.create_task.tooltip" />}
        onClick={handleOpen}
        isCreateTaskButton
      />
      <PerspectiveAttachmentDialog
        open={stage === CreateTaskStage.PERSPECTIVE_ATTACHMENT}
        onClose={handleClose}
        onNext={handleNext}
      />
      <Prompt
        open={stage === CreateTaskStage.NAME_PROMPT}
        onOk={name => {
          handleClose();

          createTask(name);
        }}
        OkButton={Dialog.Actions.CreateButton}
        onCancel={handleClose}
        emptyError="prompt.errors.name_empty"
      >
        <Dialog.Title>
          <FormattedMessageWithChallengeNaming id="portfolio.create_task.title" />
        </Dialog.Title>
        <Prompt.TextField
          label={<FormattedMessage id="portfolio.create_task.input_label" />}
        />
        <ModelSelect
          variant={ModelType.TASK}
          selectedModelId={selectedModelId}
          setSelectedModelId={modelId => setSelectedModelId(modelId)}
        />
      </Prompt>
    </RestrictedToPortfolioMode>
  );
};

export default CreateTask;
